// GA4 tracking for the public team brand page /:slug.
//
// The page already sends many events from many components. Instead of editing
// each call site, this tracker watches every event through setPageTracking:
// it stamps each one with the page load's flow_id and visitor_kind, counts the
// visitor's actions, and sends one team_page_leave per page load.
//
// ⛔ Every event counts as an action unless it is listed in PASSIVE_EVENTS.
// When you add an impression, an automatic event, or a result event to the
// team page, add it here, or it will be counted as something the visitor did.
import { trackEvent, setPageTracking } from 'utils/analytics'
import { sendLeaveOnExit } from 'utils/findTeacherTracking'

export const LEAVE_EVENT = 'team_page_leave'

export const PASSIVE_EVENTS = new Set([
  // Impressions and returns
  'section_view',
  'tab_revisit',
  'welcome_coupon_banner_impression',
  // Fires when the payment modal mounts
  'checkout_modal_open',
  // Results of an earlier action
  'ecpay_checkout_success',
  'ecpay_checkout_failed',
  'checkout_success',
  'checkout_failed',
  'login_completed',
  'booking_completed',
  LEAVE_EVENT
])

// Reaching any of these means the visitor reached a conversion step.
// A signed-out visitor who sends one of LOGIN_WALL_EVENTS only reaches the
// login wall, so it is not a completion for that page load.
export const LOGIN_WALL_EVENTS = new Set(['purchase_initiated', 'trial_claim_initiated'])

export const COMPLETION_EVENTS = new Set([
  'purchase_initiated',
  'checkout_submitted',
  'trial_claim_initiated',
  'consultation_submitted',
  'booking_completed',
  'group_class_enroll_confirm'
])

function randomId() {
  return Math.random().toString(36).slice(2, 10)
}

// @param {Object} options
// @param {Function} options.send defaults to trackEvent
// @param {Function} options.now defaults to Date.now
// @param {boolean} options.signedIn whether a viewer is signed in
export function createTeamPageTracker({ send = trackEvent, now = Date.now, signedIn = false } = {}) {
  const flowId = randomId()
  const pageStartedAt = now()
  const sectionsSeen = new Set()
  let lastSection = ''
  let actionsCount = 0
  let lastAction = 'none'
  let completed = false
  let left = false

  const summary = () => ({
    screen: 'team_page',
    seconds_on_page: Math.round((now() - pageStartedAt) / 1000),
    actions_count: actionsCount,
    last_action: lastAction,
    bounced: actionsCount === 0 && !completed,
    completed,
    section_name: lastSection,
    sections_seen: sectionsSeen.size
  })

  return {
    flowId,
    summary,
    // Called for every event the page sends. params.resumed marks an event the
    // page sent by itself after login, to finish what the visitor clicked
    // before it: it can complete the page load but is not an action.
    record(name, params = {}) {
      if (name === 'section_view' && params.section_name) {
        sectionsSeen.add(params.section_name)
        lastSection = params.section_name
      }
      const blockedByLogin = !signedIn && LOGIN_WALL_EVENTS.has(name)
      if (COMPLETION_EVENTS.has(name) && !blockedByLogin) completed = true
      if (PASSIVE_EVENTS.has(name) || params.resumed) return
      actionsCount += 1
      lastAction = name
    },
    // Once per page load. sendLeaveOnExit calls this on every hide.
    leave() {
      if (left) return
      left = true
      try {
        send(LEAVE_EVENT, {
          ...summary(),
          flow_id: flowId,
          // Survives the page unloading; a plain request would be cancelled.
          transport_type: 'beacon'
        })
      } catch (error) {
        // Analytics must never break the page.
        console.error('[Analytics] team_page_leave failed:', error)
      }
    }
  }
}

// Who is looking, decided once from the page data. First match wins: a team
// teacher can also be a student, and members browsing their own page are
// internal traffic that reports must be able to drop as one group.
// team_student means the viewer already has a relationship with the team (an
// invite link, a trial claim, or a course order), not that they have paid.
//
// @param {Object} initialData the parsed data-team-data payload
// @returns {string} team_member, team_student, signed_in or anonymous
export function visitorKindFor(initialData) {
  const user = initialData?.currentUser
  if (initialData?.isOwner || user?.isTeamMember) return 'team_member'
  if (user?.isTeamStudent) return 'team_student'
  if (user) return 'signed_in'
  return 'anonymous'
}

// Starts tracking for this page load. Call it from the page entry before
// rendering: some events fire in mount effects (ecpay_checkout_success, for
// one), and child effects run before the parent's, so a tracker created inside
// a component would miss them.
//
// @param {Object} initialData the parsed data-team-data payload
// @returns {Object} the tracker, with stop() to remove the leave listeners
export function startTeamPageTracking(initialData = {}) {
  const tracker = createTeamPageTracker({ signedIn: Boolean(initialData?.currentUser) })
  setPageTracking({
    params: { flow_id: tracker.flowId, visitor_kind: visitorKindFor(initialData) },
    onEvent: tracker.record
  })
  tracker.stop = sendLeaveOnExit(tracker)
  return tracker
}
